import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Sun, Utensils, Cookie, Moon, Apple, ChefHat } from "lucide-react";

const mealTimes = [
  {
    icon: Sun,
    title: "Breakfast",
    subtitle: "Any time you're ready",
    color: "from-amber-50 to-amber-100",
    iconColor: "text-amber-600",
    description:
      "A hearty start to the day with cereals, fresh fruit, toast, creamy porridge and a daily-changing hot option. We fit in with your morning, not the other way around. Mid-morning, expect a round of coffee and freshly baked cake."
  },
  {
    icon: Utensils,
    title: "Lunch",
    subtitle: "The heart of our day",
    color: "from-orange-50 to-orange-100",
    iconColor: "text-orange-600",
    description:
      "Residents dine together in a warm, restaurant-style setting with a choice of hot dishes, or a lighter bowl of soup and sandwiches for smaller appetites. Every lunch finishes with a homemade dessert, freshly prepared by our chef team."
  },
  {
    icon: Cookie,
    title: "Afternoon Tea",
    subtitle: "A cherished tradition",
    color: "from-rose-50 to-rose-100",
    iconColor: "text-rose-600",
    description:
      "Warm-from-the-oven cakes, a fresh brew, and good company. Afternoon tea at Barnston Court is as much a social moment as it is a meal. Residents and team members share it together, turning tea time into an activity in itself."
  },
  {
    icon: Moon,
    title: "Evening & Snacks",
    subtitle: "Around the clock comfort",
    color: "from-indigo-50 to-indigo-100",
    iconColor: "text-indigo-600",
    description:
      "After supper we serve lighter options for the evening, and fresh fruit and small treats are always available throughout the day and night, so no one ever goes hungry, whatever the hour."
  }
];

export const FoodAndDining = () => {
  return (
    <section id="dining" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-14">
          <Badge className="mb-4 bg-orange-100 text-orange-700 hover:bg-orange-200">
            Food &amp; Dining
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Freshly Prepared, Thoughtfully Served
          </h2>
          <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our dedicated chef team transforms fresh, seasonal ingredients into home-cooked, nutritionally
            balanced meals that nourish body and soul. Menus change regularly to follow the seasons, and
            residents always have several dishes to choose from at every meal. If they change their mind
            when the food arrives, we're happy to swap it. Dining with us is relaxed, flexible, and always
            an occasion.
          </p>
        </div>

        {/* Meal Times Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto mb-12">
          {mealTimes.map((meal, index) => {
            const Icon = meal.icon;
            return (
              <Card
                key={index}
                className={`border-none shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br ${meal.color} overflow-hidden`}
              >
                <CardContent className="p-6 text-center">
                  <div
                    className={`bg-white ${meal.iconColor} p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-md`}
                  >
                    <Icon className="h-8 w-8" />
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 mb-1">{meal.title}</h3>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">
                    {meal.subtitle}
                  </p>
                  <p className="text-gray-700 text-sm leading-relaxed">{meal.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Closing Statement */}
        <div className="max-w-5xl mx-auto">
          <Card className="border-2 border-teal-200 shadow-xl bg-gradient-to-r from-teal-50 via-white to-orange-50">
            <CardContent className="p-8 md:p-10">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="bg-teal-600 text-white p-5 rounded-full flex-shrink-0 shadow-lg">
                  <ChefHat className="h-10 w-10" />
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Every Meal, A Moment Worth Sharing
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Mealtimes at Barnston Court are more than just food on a plate. They're occasions,
                    opportunities for our residents to relax, enjoy great company, and be cared for by a
                    team that takes genuine pride in what ends up on the table. Dietary needs, personal
                    preferences, and changing appetites are all looked after with warmth and flexibility.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
